import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { ConnectWallet } from '../components/ConnectWallet';
import { useWeb3 } from '../context/Web3Context';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { api } from '../services/api';
import { getCategoryColor, getCategoryImage } from '../utils/helpers';
import {
  Hexagon, ShoppingCart, TrendingUp, Search, Wallet,
  BarChart3, Shield, Eye, Bell,
  Activity, Clock, CheckCircle, Zap, Globe, Lock, Star, MessageSquare,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { toast } from 'sonner';

import { iconMap, pipeline, timeAgo } from '../components/marketplace/helpers';
import { BrowseTab } from '../components/marketplace/BrowseTab';
import { HoldingsTab } from '../components/marketplace/HoldingsTab';
import { MyListingsTab } from '../components/marketplace/MyListingsTab';
import { OffersTab } from '../components/marketplace/OffersTab';
import { AlertsTab } from '../components/marketplace/AlertsTab';
import { WatchlistTab } from '../components/marketplace/WatchlistTab';
import { BuyModal } from '../components/marketplace/BuyModal';
import { OfferModal } from '../components/marketplace/OfferModal';
import { AlertModal } from '../components/marketplace/AlertModal';
import { CounterModal } from '../components/marketplace/CounterModal';
import { BuybackModal } from '../components/marketplace/BuybackModal';
import { PriceHistoryModal } from '../components/marketplace/PriceHistoryModal';
import { HoldersModal } from '../components/marketplace/HoldersModal';
import { ContactModal } from '../components/marketplace/ContactModal';
import { ListingDetailModal } from '../components/marketplace/ListingDetailModal';

export default function Marketplace() {
  const { walletAddress } = useWeb3();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  useEffect(() => {
    if (user && user.digital_twin_access !== 'active' && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // FCA FPO gate: must be HNW or Sophisticated certified to use the marketplace
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin' || user.role === 'partner') return;
    const tier = user.kyc_tier;
    const exp = user.kyc_expires_at ? new Date(user.kyc_expires_at) : null;
    const active = tier && tier !== 'none' && exp && exp > new Date();
    if (!active) {
      navigate('/onboarding/investor-certification?returnTo=/marketplace');
    }
  }, [user, navigate]);

  // ─── Data state ───────────────────────────────────────────────────────────
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [offers, setOffers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [tab, setTab] = useState('browse');
  const [watchlist, setWatchlist] = useState([]);

  // ─── Modal state ──────────────────────────────────────────────────────────
  const [buyModal, setBuyModal] = useState(null);
  const [buyAmount, setBuyAmount] = useState('1');
  const [buying, setBuying] = useState(false);
  const [detailListing, setDetailListing] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(null);
  const [offerForm, setOfferForm] = useState({ price: '', fractions: '1', message: '' });
  const [alertForm, setAlertForm] = useState({ target: '' });
  const [counterFor, setCounterFor] = useState(null);
  const [counterForm, setCounterForm] = useState({ price: '', fractions: '1', message: '' });
  const [contactFor, setContactFor] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [revalueLoading, setRevalueLoading] = useState(null);
  const [buybackFor, setBuybackFor] = useState(null);
  const [buybackQuote, setBuybackQuote] = useState(null);
  const [buybackLoading, setBuybackLoading] = useState(false);
  const [priceHistoryFor, setPriceHistoryFor] = useState(null);
  const [priceHistoryData, setPriceHistoryData] = useState([]);
  const [holdersFor, setHoldersFor] = useState(null);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleRevalue = async (tokenId) => {
    setRevalueLoading(tokenId);
    try {
      const res = await api.revalueToken(tokenId);
      const pct = res.data?.change_percent ?? 0;
      const dir = pct >= 0 ? 'up' : 'down';
      toast.success(`Revalued: ${dir} ${Math.abs(pct).toFixed(2)}% (now ${formatCurrency(res.data?.new_price_per_fraction)} per fraction)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revaluation failed');
    } finally { setRevalueLoading(null); }
  };

  const openBuyback = async (token) => {
    setBuybackFor(token);
    setBuybackQuote(null);
    try {
      const res = await api.getBuybackQuote(token.id);
      setBuybackQuote(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quote');
    }
  };

  const submitBuyback = async () => {
    if (!buybackFor) return;
    setBuybackLoading(true);
    try {
      if (buybackQuote?.fractions_outstanding === 0) {
        await api.burnUnsoldToken(buybackFor.id);
        toast.success(`${buybackFor.asset_name} un-tokenised. You hold 100% again.`);
        setBuybackFor(null);
        fetchData();
        return;
      }
      const res = await api.startBuyback(buybackFor.id);
      if (res.data?.checkout_url) {
        toast.success('Redirecting to secure payment...');
        setTimeout(() => { window.location.href = res.data.checkout_url; }, 400);
      } else if (res.data?.burned_immediately) {
        toast.success('Un-tokenised.');
        setBuybackFor(null);
        fetchData();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Buyback failed');
    } finally { setBuybackLoading(false); }
  };

  const openPriceHistory = async (token) => {
    setPriceHistoryFor(token);
    setPriceHistoryData([]);
    try {
      const res = await api.getTokenPriceHistory(token.id);
      setPriceHistoryData(res.data?.points || []);
    } catch (e) {
      toast.error('Could not load price history');
    }
  };

  const handleBuy = async () => {
    if (!buyModal) return;
    const fracs = parseInt(buyAmount);
    if (!fracs || fracs <= 0) { toast.error('Enter a valid number of fractions'); return; }
    setBuying(true);
    try {
      const res = await api.createPurchaseCheckout(buyModal.id, fracs);
      if (res.data?.checkout_url) {
        toast.success('Redirecting to secure payment...');
        setTimeout(() => { window.location.href = res.data.checkout_url; }, 500);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment service unavailable. Please try again.');
    }
    finally { setBuying(false); }
  };

  const toggleWatchlist = (id) => {
    setWatchlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    toast.success(watchlist.includes(id) ? 'Removed from watchlist' : 'Added to watchlist');
  };

  // Handle Stripe payment return (poll checkout status for deployment safety)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchase') === 'success') {
      const sessionId = params.get('session_id');
      if (sessionId) {
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const res = await api.getMarketplaceCheckoutStatus(sessionId);
            if (res.data?.payment_status === 'paid') {
              toast.success('Payment confirmed. Your fractions have been credited.');
              setTab('holdings');
              fetchData();
              return;
            }
          } catch {}
          if (attempts < 6) setTimeout(poll, 2000);
          else toast.info('Payment is still processing. Check back shortly.');
        };
        poll();
      } else {
        toast.success('Payment complete.');
      }
      window.history.replaceState({}, '', '/marketplace');
    } else if (params.get('purchase') === 'cancelled') {
      toast.error('Payment was cancelled.');
      window.history.replaceState({}, '', '/marketplace');
    } else if (params.get('buyback') === 'success') {
      const sessionId = params.get('session_id');
      if (sessionId) {
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const res = await api.getBuybackStatus(sessionId);
            if (res.data?.payment_status === 'paid') {
              toast.success('Un-tokenisation complete. Holders have been paid out.');
              setTab('mylistings');
              fetchData();
              return;
            }
          } catch {}
          if (attempts < 6) setTimeout(poll, 2000);
        };
        poll();
      }
      window.history.replaceState({}, '', '/marketplace');
    } else if (params.get('buyback') === 'cancelled') {
      const sessionId = params.get('session_id');
      const tokenId = params.get('token_id');
      if (tokenId) {
        api.cancelBuyback(tokenId)
          .then(() => toast.info('Buyback cancelled. You can try again whenever you like.'))
          .catch(() => toast.info('Buyback cancelled.'))
          .finally(() => fetchData());
      } else if (sessionId) {
        api.getBuybackStatus(sessionId).catch(() => {});
        toast.info('Buyback cancelled.');
        fetchData();
      } else {
        toast.info('Buyback cancelled.');
      }
      window.history.replaceState({}, '', '/marketplace');
    }
  }, []);

  useEffect(() => { fetchData(); }, [category, sortBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listingsRes, myListingsRes, holdingsRes, statsRes, activityRes, perfRes, offersRes, alertsRes] = await Promise.all([
        api.getMarketplaceListings(category, sortBy),
        api.getMyListings().catch(() => ({ data: [] })),
        api.getMarketplaceHoldings(),
        api.getMarketplaceStats(),
        api.getMarketplaceActivity().catch(() => ({ data: [] })),
        api.getHoldingsPerformance().catch(() => ({ data: null })),
        api.listOffers('all').catch(() => ({ data: [] })),
        api.listFractionAlerts().catch(() => ({ data: [] })),
      ]);
      setListings(listingsRes.data);
      setMyListings(myListingsRes.data || []);
      setHoldings(holdingsRes.data);
      setStats(statsRes.data);
      setActivity(activityRes.data || []);
      setPerformance(perfRes.data);
      setOffers(offersRes.data || []);
      setAlerts(alertsRes.data || []);
    } catch (err) {
      console.error('Marketplace fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="marketplace-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Hexagon size={24} className="text-[#627EEA]" />
              <p className="text-[#627EEA] uppercase tracking-[0.2em] text-xs">Ethereum Powered</p>
            </div>
            <h1 className="font-serif text-3xl text-white">Digital Twin Marketplace</h1>
            <p className="text-muted-foreground mt-1 text-sm">Buy and sell fractional ownership of tokenized luxury assets</p>
          </div>
          <ConnectWallet />
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Listed Assets', value: stats.total_listed, icon: ShoppingCart },
              { label: 'Total Minted', value: stats.total_minted, icon: Hexagon },
              { label: 'Sales', value: stats.total_trades, icon: TrendingUp },
              { label: 'Market Value', value: formatCurrency(stats.total_market_value), icon: BarChart3 },
            ].map((s, i) => (
              <div key={i} className="luxury-card p-4" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <s.icon size={14} className="text-[#627EEA]" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
                </div>
                <p className="text-xl text-white font-semibold">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Featured Digital Twin + Activity Feed */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-2 luxury-card overflow-hidden" data-testid="featured-twin">
            <div className="relative h-48">
              <img src={getCategoryImage('car')} alt="Featured" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-gold/20 backdrop-blur-sm border border-gold/30 rounded-full">
                <Star size={10} className="text-gold" />
                <span className="text-[10px] text-gold uppercase tracking-wider">Featured</span>
              </div>
              <div className="absolute bottom-4 left-5 right-5">
                <h3 className="text-white font-serif text-xl">1965 Ferrari 275 GTB</h3>
                <p className="text-gold text-lg font-semibold">£3,200,000</p>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-4 mb-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 rounded cursor-help">
                      <Shield size={10} className="text-emerald-400" />
                      <span className="text-[10px] text-emerald-400 uppercase">Verified</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
                    <p className="text-xs">Physically inspected and authenticated by certified specialists.</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-gold/10 rounded cursor-help">
                      <Lock size={10} className="text-gold" />
                      <span className="text-[10px] text-gold uppercase">Insured</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
                    <p className="text-xs">Full replacement value covered by Lloyd's of London syndicate.</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-[#627EEA]/10 rounded cursor-help">
                      <Globe size={10} className="text-[#627EEA]" />
                      <span className="text-[10px] text-[#627EEA] uppercase">On-Chain</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
                    <p className="text-xs">Ownership verified on Ethereum. Token ID permanently recorded.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                One of just 280 produced. Matching numbers, Ferrari Classiche certified. Stored in climate-controlled vault, London. 1,000 fractions available at £3,200 each.
              </p>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="luxury-card p-4" data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
              <Activity size={14} className="text-gold" />
              <span className="text-xs text-gold uppercase tracking-wider">Recent Activity</span>
            </div>
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No marketplace activity yet. Be the first to list an asset.</p>
              ) : activity.map((item, i) => {
                const Icon = iconMap[item.category] || Hexagon;
                return (
                  <div key={i} className="flex items-start gap-3 pb-3 border-b border-[#1A1A1A] last:border-0" data-testid={`activity-${i}`}>
                    <div className="w-7 h-7 rounded bg-[#0F0F0F] flex items-center justify-center flex-shrink-0">
                      <Icon size={12} className={getCategoryColor(item.category)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{item.asset}</p>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className={`text-[10px] ${item.action === 'Fraction Sold' ? 'text-emerald-400' : item.action === 'New Listing' ? 'text-[#627EEA]' : 'text-gold'}`}>
                          {item.action}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(item.time)}</span>
                      </div>
                      {item.value && (
                        <span className="text-[10px] text-muted-foreground">{formatCurrency(item.value)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Why Fractional Ownership */}
        <div className="luxury-card p-6 mb-8" data-testid="why-fractional">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={14} className="text-gold" />
            <h3 className="text-sm text-gold uppercase tracking-wider">Why Fractional Ownership?</h3>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#627EEA]/10 flex items-center justify-center">
                <TrendingUp size={18} className="text-[#627EEA]" />
              </div>
              <p className="text-xs text-white font-medium">Diversify</p>
              <p className="text-[10px] text-muted-foreground mt-1">Own pieces of multiple assets</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/10 flex items-center justify-center">
                <Star size={18} className="text-gold" />
              </div>
              <p className="text-xs text-white font-medium">Access</p>
              <p className="text-[10px] text-muted-foreground mt-1">Invest in assets beyond reach</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-400/10 flex items-center justify-center">
                <Activity size={18} className="text-emerald-400" />
              </div>
              <p className="text-xs text-white font-medium">Liquidity</p>
              <p className="text-[10px] text-muted-foreground mt-1">Sell fractions anytime, 24/7</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/10 flex items-center justify-center">
                <Shield size={18} className="text-gold" />
              </div>
              <p className="text-xs text-white font-medium">No Middleman</p>
              <p className="text-[10px] text-muted-foreground mt-1">Direct peer-to-peer ownership</p>
            </div>
          </div>
        </div>

        {/* Wallet Info */}
        <div className="luxury-card p-5 mb-8 flex items-center gap-5" data-testid="wallet-info">
          <div className="w-10 h-10 rounded-lg bg-[#F6851B]/10 flex items-center justify-center flex-shrink-0">
            <Wallet size={20} className="text-[#F6851B]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white text-sm font-medium">Connect Your Wallet</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Use MetaMask or any Ethereum-compatible wallet to buy fractions. Your wallet is your identity on the marketplace. All purchases settle directly to the seller's connected payouts, no middleman.
            </p>
          </div>
          {!walletAddress && <ConnectWallet />}
          {walletAddress && (
            <div className="text-right flex-shrink-0">
              <p className="text-emerald-400 text-xs flex items-center gap-1"><CheckCircle size={10} /> Connected</p>
              <p className="text-[10px] text-muted-foreground font-mono">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0A0A0A] border border-[#262626] p-1 rounded w-fit">
          {[
            { key: 'browse', label: 'Marketplace', icon: Search },
            { key: 'mylistings', label: 'My Listings', icon: Hexagon },
            { key: 'holdings', label: 'My Holdings', icon: Wallet },
            { key: 'offers', label: 'Offers', icon: MessageSquare },
            { key: 'alerts', label: 'Price Alerts', icon: Bell },
            { key: 'watchlist', label: 'Watchlist', icon: Eye },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded transition-colors ${
                tab === t.key ? 'bg-[#627EEA] text-white' : 'text-muted-foreground hover:text-white hover:bg-white/5'
              }`}
            >
              <t.icon size={14} /> {t.label}
              {t.key === 'watchlist' && watchlist.length > 0 && (
                <span className="ml-1 text-[10px] bg-gold text-black px-1.5 rounded-full">{watchlist.length}</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'browse' && (
          <BrowseTab
            loading={loading} listings={listings}
            category={category} setCategory={setCategory}
            sortBy={sortBy} setSortBy={setSortBy}
            watchlist={watchlist} toggleWatchlist={toggleWatchlist}
            setBuyModal={setBuyModal} setDetailListing={setDetailListing}
            formatCurrency={formatCurrency}
          />
        )}
        {tab === 'holdings' && (
          <HoldingsTab holdings={holdings} performance={performance} formatCurrency={formatCurrency} />
        )}
        {tab === 'mylistings' && (
          <MyListingsTab
            myListings={myListings}
            setHoldersFor={setHoldersFor}
            openPriceHistory={openPriceHistory}
            handleRevalue={handleRevalue}
            openBuyback={openBuyback}
            revalueLoading={revalueLoading}
            formatCurrency={formatCurrency}
          />
        )}
        {tab === 'offers' && (
          <OffersTab
            offers={offers} fetchData={fetchData}
            setCounterFor={setCounterFor} setCounterForm={setCounterForm}
            formatCurrency={formatCurrency}
          />
        )}
        {tab === 'alerts' && (
          <AlertsTab alerts={alerts} fetchData={fetchData} formatCurrency={formatCurrency} />
        )}
        {tab === 'watchlist' && (
          <WatchlistTab
            watchlist={watchlist} listings={listings}
            setBuyModal={setBuyModal} toggleWatchlist={toggleWatchlist}
            setTab={setTab} formatCurrency={formatCurrency}
          />
        )}

        {/* Coming Soon Pipeline */}
        <div className="mt-10 luxury-card p-5" data-testid="coming-soon">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={14} className="text-gold" />
            <span className="text-xs text-gold uppercase tracking-wider">Coming Soon</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {pipeline.map((item, i) => {
              const Icon = iconMap[item.category] || Hexagon;
              return (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded">
                  <Icon size={16} className={getCategoryColor(item.category)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs truncate">{item.name}</p>
                    <p className="text-[10px] text-muted-foreground">{item.status}</p>
                  </div>
                  <span className="text-[10px] text-gold bg-gold/10 px-2 py-0.5 rounded">{item.eta}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Modals */}
        <BuyModal
          buyModal={buyModal} setBuyModal={setBuyModal}
          buyAmount={buyAmount} setBuyAmount={setBuyAmount}
          buying={buying} handleBuy={handleBuy}
          formatCurrency={formatCurrency}
        />
        <OfferModal
          showOfferModal={showOfferModal} setShowOfferModal={setShowOfferModal}
          offerForm={offerForm} setOfferForm={setOfferForm}
          setTab={setTab} fetchData={fetchData}
          formatCurrency={formatCurrency}
        />
        <AlertModal
          showAlertModal={showAlertModal} setShowAlertModal={setShowAlertModal}
          alertForm={alertForm} setAlertForm={setAlertForm}
          fetchData={fetchData} formatCurrency={formatCurrency}
        />
        <CounterModal
          counterFor={counterFor} setCounterFor={setCounterFor}
          counterForm={counterForm} setCounterForm={setCounterForm}
          fetchData={fetchData} formatCurrency={formatCurrency}
        />
        <BuybackModal
          buybackFor={buybackFor} setBuybackFor={setBuybackFor}
          buybackQuote={buybackQuote} setBuybackQuote={setBuybackQuote}
          buybackLoading={buybackLoading} submitBuyback={submitBuyback}
          formatCurrency={formatCurrency}
        />
        <PriceHistoryModal
          priceHistoryFor={priceHistoryFor} setPriceHistoryFor={setPriceHistoryFor}
          priceHistoryData={priceHistoryData}
        />
        <HoldersModal
          holdersFor={holdersFor} setHoldersFor={setHoldersFor}
          formatCurrency={formatCurrency}
        />
        <ContactModal
          contactFor={contactFor} setContactFor={setContactFor}
          contactMessage={contactMessage} setContactMessage={setContactMessage}
          contactSending={contactSending} setContactSending={setContactSending}
          formatCurrency={formatCurrency}
        />
        <ListingDetailModal
          detailListing={detailListing} setDetailListing={setDetailListing}
          setBuyModal={setBuyModal}
          setOfferForm={setOfferForm} setShowOfferModal={setShowOfferModal}
          setAlertForm={setAlertForm} setShowAlertModal={setShowAlertModal}
          setContactFor={setContactFor} setContactMessage={setContactMessage}
          formatCurrency={formatCurrency}
        />
      </div>
    </DashboardLayout>
  );
}
